import { FilterFn } from '@tanstack/react-table';

export interface DateRangeFilterValue {
  from: string | null;
  to: string | null;
}

const isEmptyRange = (value: DateRangeFilterValue | undefined) =>
  !value || (!value.from && !value.to);

// Renders a Date in the browser's local timezone as YYYY-MM-DD, matching the
// timezone-less value a native <input type="date"> produces for `from`/`to`.
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Filters an ISO date/datetime column against a { from, to } range. Both
// bounds are inclusive and optional. The cell value may be a date-only or
// full UTC datetime ISO string — it's parsed and re-expressed in the
// browser's local timezone before comparison so a datetime near local
// midnight (e.g. 9pm Pacific, already past midnight UTC) still lands on the
// calendar day the user actually picked, instead of the UTC day.
export const isWithinDateRange = (
  raw: string | null | undefined,
  filterValue: DateRangeFilterValue | undefined
): boolean => {
  if (isEmptyRange(filterValue)) return true;
  if (!raw) return false;

  // A bare "YYYY-MM-DD" has no time/zone component — treat it as the literal
  // calendar day rather than parsing it (JS treats date-only ISO strings as
  // UTC midnight, which would shift it a day when read back in local time).
  let cellDate: string;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    cellDate = raw;
  } else {
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return false;
    cellDate = toLocalDateString(parsed);
  }

  if (filterValue!.from && cellDate < filterValue!.from) return false;
  if (filterValue!.to && cellDate > filterValue!.to) return false;
  return true;
};

export const dateRangeFilterFn: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: DateRangeFilterValue
) => isWithinDateRange(row.getValue<string | null>(columnId), filterValue);

dateRangeFilterFn.autoRemove = (value: DateRangeFilterValue) =>
  isEmptyRange(value);
